/**
 * Normalize API / socket point to { lat, lng, datetime }.
 */
export function normalizePathPoint(raw) {
  if (!raw) return null;

  const lat = Number.parseFloat(raw.lat ?? raw.latitude);
  const lng = Number.parseFloat(raw.lng ?? raw.longitude);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  return {
    lat,
    lng,
    datetime:
      raw.datetime ??
      raw.arrivedAt ??
      raw.arrived_at ??
      raw.created_at ??
      new Date().toISOString(),
  };
}

/**
 * Parse ReceiveTripUpdate payload (JSON string or object, snake_case or camelCase).
 */
export function parseTripUpdate(data) {
  let parsed = data;
  if (typeof data === "string") {
    try {
      parsed = JSON.parse(data);
    } catch {
      return null;
    }
  }

  if (!parsed || typeof parsed !== "object") return null;

  const latitude = Number.parseFloat(parsed.latitude ?? parsed.lat);
  const longitude = Number.parseFloat(parsed.longitude ?? parsed.lng);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

  return {
    latitude,
    longitude,
    datetime: parsed.datetime ?? new Date().toISOString(),
    arrivedTripPointId:
      parsed.arrived_trip_point_id ?? parsed.arrivedTripPointId ?? null,
    tripId: parsed.trip_id ?? parsed.tripId ?? null,
  };
}

const COORD_EPSILON = 0.0001;

export function coordsKey(lat, lng) {
  return `${lat.toFixed(5)}_${lng.toFixed(5)}`;
}

export function isSameCoords(a, b) {
  if (!a || !b) return false;
  const latA = Number.parseFloat(a.lat ?? a.latitude);
  const lngA = Number.parseFloat(a.lng ?? a.longitude);
  const latB = Number.parseFloat(b.lat ?? b.latitude);
  const lngB = Number.parseFloat(b.lng ?? b.longitude);
  if (!Number.isFinite(latA) || !Number.isFinite(lngA)) return false;
  if (!Number.isFinite(latB) || !Number.isFinite(lngB)) return false;
  return (
    Math.abs(latA - latB) < COORD_EPSILON &&
    Math.abs(lngA - lngB) < COORD_EPSILON
  );
}

/** Match Flutter: string equality on lat/lng when available, else numeric epsilon. */
export function pointsMatchByCoordinates(p1, p2) {
  if (!p1 || !p2) return false;

  const rawLat1 = p1.lat ?? p1.latitude;
  const rawLng1 = p1.lng ?? p1.longitude;
  const rawLat2 = p2.lat ?? p2.latitude;
  const rawLng2 = p2.lng ?? p2.longitude;

  if (
    rawLat1 != null &&
    rawLng1 != null &&
    rawLat2 != null &&
    rawLng2 != null &&
    String(rawLat1) === String(rawLat2) &&
    String(rawLng1) === String(rawLng2)
  ) {
    return true;
  }

  return isSameCoords(p1, p2);
}

/**
 * Sort by datetime and remove consecutive duplicate coordinates.
 */
export function sortAndDedupeTrail(points) {
  const sorted = [...(points || [])].sort(
    (a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime(),
  );

  const result = [];
  for (const point of sorted) {
    const last = result[result.length - 1];
    if (last && isSameCoords(last, point)) continue;
    result.push(point);
  }
  return result;
}

/**
 * Merge API path with live socket tail (only points newer than last API point).
 */
export function mergeServerPathWithLiveTail(fromApi, current) {
  const api = sortAndDedupeTrail(fromApi);
  const live = sortAndDedupeTrail(current);

  if (!api.length) return live;
  if (!live.length) return api;

  const lastApi = api[api.length - 1];
  const lastApiTime = new Date(lastApi.datetime).getTime();
  const apiKeys = new Set(api.map((p) => coordsKey(p.lat, p.lng)));

  const tail = live.filter((p) => {
    const pTime = new Date(p.datetime).getTime();
    if (pTime > lastApiTime) return true;
    if (
      pTime === lastApiTime &&
      !isSameCoords(p, lastApi) &&
      !apiKeys.has(coordsKey(p.lat, p.lng))
    ) {
      return true;
    }
    return false;
  });

  return sortAndDedupeTrail([...api, ...tail]);
}

/**
 * Append socket update; update last point if same coordinates.
 */
export function appendPathPoint(trail, update) {
  const point = normalizePathPoint({
    lat: update.latitude ?? update.lat,
    lng: update.longitude ?? update.lng,
    datetime: update.datetime,
  });

  if (!point) return trail;

  const next = [...trail];
  const last = next[next.length - 1];

  if (last && isSameCoords(last, point)) {
    next[next.length - 1] = {
      ...last,
      datetime: point.datetime,
    };
    return next;
  }

  next.push(point);
  return sortAndDedupeTrail(next);
}

export function pointsToLatLngArray(points) {
  return sortAndDedupeTrail(points).map(({ lat, lng }) => ({ lat, lng }));
}

export function isActiveTripStatus(status) {
  return String(status || "").toLowerCase() === "on_going";
}

export function isCompletedTripStatus(status) {
  return String(status || "").toLowerCase() === "completed";
}

function normalizeTripPoint(raw, addedByFallback = null) {
  if (!raw) return null;
  const lat = Number.parseFloat(raw.lat ?? raw.latitude);
  const lng = Number.parseFloat(raw.lng ?? raw.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const addedBy =
    raw.added_by ?? raw.addedBy ?? raw.user_id ?? raw.userId ?? addedByFallback;

  return {
    lat,
    lng,
    latitude: String(raw.latitude ?? raw.lat ?? lat),
    longitude: String(raw.longitude ?? raw.lng ?? lng),
    addedBy: addedBy != null ? addedBy : null,
    name: raw.name_en ?? raw.name_ar ?? raw.name ?? "",
  };
}

function pointFromFlatCoords(latRaw, lngRaw, passengerId, name = "") {
  if (latRaw == null || lngRaw == null) return null;
  return normalizeTripPoint(
    { latitude: latRaw, longitude: lngRaw, name_en: name },
    passengerId,
  );
}

function pushUniquePoint(target, seen, point) {
  if (!point || point.addedBy == null) return;
  const key = `${point.addedBy}_${coordsKey(point.lat, point.lng)}`;
  if (seen.has(key)) return;
  seen.add(key);
  target.push(point);
}

/**
 * Extract passenger trip points from trip payload (activeTrip.points + requests).
 */
export function extractTripPassengerPoints(tripData) {
  if (!tripData) return [];

  const merged = [];
  const seen = new Set();

  const addList = (list, addedByFallback = null) => {
    if (!Array.isArray(list)) return;
    for (const raw of list) {
      const point = normalizeTripPoint(raw, addedByFallback);
      pushUniquePoint(merged, seen, point);
    }
  };

  const pointSources = [
    tripData.active_trip?.points,
    tripData.activeTrip?.points,
    tripData.last_active_trip?.points,
    tripData.info?.active_trip?.points,
    tripData.info?.activeTrip?.points,
    tripData.points,
    tripData.trip_points,
    tripData.info?.points,
  ];

  for (const source of pointSources) {
    addList(source);
  }

  const requests = tripData.requests ?? tripData.trip_requests ?? [];
  for (const req of requests) {
    const passengerId =
      req.user_id ??
      req.passenger_id ??
      req.passenger?.id ??
      req.added_by;

    if (passengerId == null) continue;

    addList(req.points, passengerId);
    addList(req.trip_points, passengerId);

    const boarding = req.boarding_point ?? req.pickup_point ?? req.start_point;
    const dropoff =
      req.dropoff_point ?? req.drop_off_point ?? req.end_point ?? req.destination_point;

    pushUniquePoint(
      merged,
      seen,
      normalizeTripPoint(boarding, passengerId),
    );
    pushUniquePoint(
      merged,
      seen,
      normalizeTripPoint(dropoff, passengerId),
    );

    pushUniquePoint(
      merged,
      seen,
      pointFromFlatCoords(
        req.pickup_latitude ?? req.pickup_lat ?? req.boarding_latitude,
        req.pickup_longitude ?? req.pickup_lng ?? req.boarding_longitude,
        passengerId,
        "Pickup",
      ),
    );
    pushUniquePoint(
      merged,
      seen,
      pointFromFlatCoords(
        req.dropoff_latitude ?? req.dropoff_lat ?? req.drop_off_latitude,
        req.dropoff_longitude ?? req.dropoff_lng ?? req.drop_off_longitude,
        passengerId,
        "Drop-off",
      ),
    );
  }

  return merged;
}

/**
 * Driver/admin view (Flutter _loadAllPassengerPoints):
 * exclude start/end → group by added_by → sort by original index → first=boarding, last=dropoff.
 */
export function buildPassengerBoardingMarkers(
  tripPoints,
  startPoint,
  endPoint,
  passengers = [],
) {
  const filtered = (tripPoints || [])
    .filter((point) => {
      if (point.addedBy == null) return false;
      return (
        !pointsMatchByCoordinates(point, startPoint) &&
        !pointsMatchByCoordinates(point, endPoint)
      );
    })
    .map((point, index) => ({ point, index }));

  const grouped = new Map();
  for (const { point, index } of filtered) {
    const id = point.addedBy;
    if (!grouped.has(id)) grouped.set(id, []);
    grouped.get(id).push({ point, index });
  }

  const passengerNameById = new Map();
  for (const p of passengers || []) {
    const ids = [
      p.user_id,
      p.passenger_id,
      p.id,
      p.passenger?.id,
    ].filter((v) => v != null);
    const name = p.full_name ?? p.name ?? p.passenger?.name ?? `Passenger ${p.id}`;
    for (const id of ids) {
      passengerNameById.set(id, name);
      passengerNameById.set(String(id), name);
      passengerNameById.set(Number(id), name);
    }
  }

  const markers = [];

  for (const [passengerId, list] of grouped.entries()) {
    if (!list.length) continue;

    list.sort((a, b) => a.index - b.index);

    const boardingPoint = list[0].point;
    const dropOffPoint =
      list.length > 1 ? list[list.length - 1].point : boardingPoint;

    const passengerName =
      passengerNameById.get(passengerId) ??
      passengerNameById.get(String(passengerId)) ??
      passengerNameById.get(Number(passengerId)) ??
      `Passenger ${passengerId}`;

    markers.push({
      id: `passenger_${passengerId}_boarding`,
      lat: boardingPoint.lat,
      lng: boardingPoint.lng,
      type: "boarding",
      title: `Boarding — ${passengerName}`,
      label: "↑",
    });

    if (
      list.length > 1 &&
      !pointsMatchByCoordinates(dropOffPoint, boardingPoint)
    ) {
      markers.push({
        id: `passenger_${passengerId}_dropoff`,
        lat: dropOffPoint.lat,
        lng: dropOffPoint.lng,
        type: "dropoff",
        title: `Drop-off — ${passengerName}`,
        label: "↓",
      });
    }
  }

  return markers;
}

/**
 * Planned route polyline from trip.info.compressed_path
 */
export function getPlannedRouteEncoded(tripData) {
  return (
    tripData?.info?.compressed_path ??
    tripData?.compressed_path ??
    tripData?.compressedPath ??
    ""
  );
}
